import { CalendarDays, MapPin, TriangleAlert, UserRound } from "lucide-react";
import OfficialStatusBadge from "@/components/OfficialStatusBadge";
import { formatBusinessDate } from "@/utils/datetime";

const stageLabel = (value) => {
  const labels = {
    blocked: "Roadblocked",
    canceled: "Canceled",
    closed: "Closed",
    complete: "Complete",
    in_progress: "In Progress",
    not_started: "Not Started",
  };
  return labels[value] || (value || "Not set").replaceAll("_", " ");
};

const displayDate = (value) => (value ? formatBusinessDate(value) : "Not scheduled");

const SummaryValue = ({ icon: Icon, label, testId, value }) => (
  <div className="command-summary-value" data-testid={testId}>
    <Icon size={14} />
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const InformationValue = ({ label, testId, value }) => (
  <div className="project-information-value" data-testid={testId}>
    <span>{label}</span>
    <p>{value || "Not documented"}</p>
  </div>
);

export const ProjectCommandSummary = ({
  companyLabels,
  managers,
  project,
}) => {
  const roadblockReason = project.operational_blocker_reason?.trim();

  return (
    <section className="command-panel command-summary-panel" data-testid="command-project-summary">
    <div className="command-panel-heading">
      <div>
        <span className="operations-kicker">Project Summary</span>
        <h2>Project details</h2>
      </div>
      <OfficialStatusBadge
        label={stageLabel(project.lifecycle_state)}
        state={project.official_color_state}
        testId="command-project-stage"
      />
    </div>
    <div className="command-project-identity">
      <strong data-testid="command-project-name">{project.name}</strong>
      <span data-testid="command-project-customer">
        {project.project_code !== project.name && `${project.project_code} · `}
        {project.client || "Customer unassigned"}
      </span>
    </div>
    <div className="command-summary-grid">
      <SummaryValue
        icon={MapPin}
        label="City"
        testId="command-project-city"
        value={project.city || "Not documented"}
      />
      <SummaryValue
        icon={CalendarDays}
        label="Due Date"
        testId="command-project-due-date"
        value={displayDate(project.due_date)}
      />
      <SummaryValue
        icon={CalendarDays}
        label="Scheduled Date"
        testId="command-project-scheduled-date"
        value={displayDate(project.scheduled_date)}
      />
      <SummaryValue
        icon={UserRound}
        label={companyLabels.projectManager}
        testId="command-project-manager"
        value={managers || "Unassigned"}
      />
      <SummaryValue
        icon={UserRound}
        label="Customer Supervisor"
        testId="command-customer-supervisor"
        value={project.customer_supervisor || "Unassigned"}
      />
    </div>
    {project.operational_blocker && (
      <div className="command-roadblock-inline" data-testid="command-roadblock-inline">
        <TriangleAlert size={15} />
        <div>
          <strong>Roadblocks</strong>
          <span>{roadblockReason && roadblockReason !== "." ? roadblockReason : "Roadblock detail recorded"}</span>
        </div>
      </div>
    )}
    <section className="project-information-section" data-testid="project-information-section">
      <div className="project-information-heading">
        <span className="operations-kicker">Project Information</span>
        <h3>Stored project record</h3>
      </div>
      <div className="project-information-grid">
        <InformationValue
          label="Project Number"
          testId="project-information-number"
          value={project.project_code}
        />
        <InformationValue
          label="Work Scope"
          testId="project-information-work-scope"
          value={project.work_scope}
        />
        <InformationValue
          label="Remaining Work"
          testId="project-information-remaining-work"
          value={project.remaining_work}
        />
      </div>
    </section>
    </section>
  );
};